import React, { useEffect, useState, useCallback } from 'react';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { fundraiseService } from '../services/fundraiseService';
import {
  Pipeline,
  Fund,
  FundraiseStats,
  CreatePipelineInput,
  UpdatePipelineInput,
  CreateFundInput,
  UpdateFundInput,
  PipelineShareLink,
  PipelineSharePermission,
  PipelineCollaborator,
  ACTIVE_DISCUSSION_STAGES,
} from '../types/fundraise';
import { PageHeader } from '../components/common';
import { StatCard } from '../components/dashboard';
import PipelineSection from '../components/fundraise/PipelineSection';
import CreatePipelineModal from '../components/fundraise/CreatePipelineModal';
import EditPipelineModal from '../components/fundraise/EditPipelineModal';
import AddFundModal from '../components/fundraise/AddFundModal';
import FundDetailModal from '../components/fundraise/FundDetailModal';
import PipelineShareModal from '../components/fundraise/PipelineShareModal';
import DeleteConfirmModal from '../components/fundraise/DeleteConfirmModal';
import { formatCurrency } from '../utils/helpers';
import {
  FiTarget,
  FiLayers,
  FiDollarSign,
  FiMessageCircle,
  FiPlus,
} from 'react-icons/fi';

// Type-safe icon wrappers
const TargetIcon = FiTarget as React.FC<React.SVGProps<SVGSVGElement>>;
const LayersIcon = FiLayers as React.FC<React.SVGProps<SVGSVGElement>>;
const DollarIcon = FiDollarSign as React.FC<React.SVGProps<SVGSVGElement>>;
const MessageIcon = FiMessageCircle as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;

const Fundraise: React.FC = () => {
  const { isLoading: authLoading, error: authError, canAccessApp } = useAuthStatus();

  // Data state
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [fundsByPipeline, setFundsByPipeline] = useState<Record<string, Fund[]>>({});
  const [stats, setStats] = useState<FundraiseStats>({
    total_pipelines: 0,
    total_funds: 0,
    combined_target: 0,
    active_discussions: 0,
  });

  // Loading and error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [addingFundToPipeline, setAddingFundToPipeline] = useState<Pipeline | null>(null);
  const [viewingFund, setViewingFund] = useState<Fund | null>(null);
  const [sharingPipeline, setSharingPipeline] = useState<Pipeline | null>(null);
  const [shareLinks, setShareLinks] = useState<PipelineShareLink[]>([]);
  const [collaborators, setCollaborators] = useState<PipelineCollaborator[]>([]);
  const [deletingPipeline, setDeletingPipeline] = useState<Pipeline | null>(null);
  const [deletingFund, setDeletingFund] = useState<Fund | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Calculate stats from data
  const calculateStats = useCallback((pipelinesData: Pipeline[], fundsData: Record<string, Fund[]>): FundraiseStats => {
    let totalFunds = 0;
    let combinedTarget = 0;
    let activeDiscussions = 0;

    pipelinesData.forEach((pipeline) => {
      combinedTarget += pipeline.target_amount;
      const pipelineFunds = fundsData[pipeline.id] || [];
      totalFunds += pipelineFunds.length;

      // Count funds in active discussion stages
      pipelineFunds.forEach((fund) => {
        if (ACTIVE_DISCUSSION_STAGES.includes(fund.stage)) {
          activeDiscussions++;
        }
      });
    });

    return {
      total_pipelines: pipelinesData.length,
      total_funds: totalFunds,
      combined_target: combinedTarget,
      active_discussions: activeDiscussions,
    };
  }, []);

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch pipelines
      const pipelinesData = await fundraiseService.getPipelines();

      // Fetch funds for each pipeline
      const fundsMap: Record<string, Fund[]> = {};
      await Promise.all(
        pipelinesData.map(async (pipeline) => {
          try {
            const funds = await fundraiseService.getFunds(pipeline.id);
            fundsMap[pipeline.id] = funds;
          } catch (err) {
            console.error(`Failed to fetch funds for pipeline ${pipeline.id}:`, err);
            fundsMap[pipeline.id] = [];
          }
        })
      );

      setPipelines(pipelinesData);
      setFundsByPipeline(fundsMap);
      setStats(calculateStats(pipelinesData, fundsMap));
    } catch (err: any) {
      console.error('Failed to load fundraise data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [calculateStats]);

  useEffect(() => {
    if (authLoading || authError || !canAccessApp) return;
    loadData();
  }, [authLoading, authError, canAccessApp, loadData]);

  // Pipeline handlers
  const handleCreatePipeline = async (input: CreatePipelineInput) => {
    await fundraiseService.createPipeline(input);
    await loadData();
  };

  const handleUpdatePipeline = async (id: string, input: UpdatePipelineInput) => {
    await fundraiseService.updatePipeline(id, input);
    await loadData();
  };

  const handleDeletePipeline = async () => {
    if (!deletingPipeline) return;
    setIsDeleting(true);
    try {
      await fundraiseService.deletePipeline(deletingPipeline.id);
      setDeletingPipeline(null);
      await loadData();
    } catch (err) {
      console.error('Failed to delete pipeline:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTogglePipelineExpand = async (pipelineId: string, isExpanded: boolean) => {
    // Optimistic update
    setPipelines((prev) =>
      prev.map((p) => (p.id === pipelineId ? { ...p, is_expanded: isExpanded } : p))
    );

    try {
      await fundraiseService.togglePipelineExpanded(pipelineId, isExpanded);
    } catch (err) {
      // Revert on error
      setPipelines((prev) =>
        prev.map((p) => (p.id === pipelineId ? { ...p, is_expanded: !isExpanded } : p))
      );
      console.error('Failed to toggle pipeline:', err);
    }
  };

  // Fund handlers
  const handleAddFund = async (input: CreateFundInput) => {
    await fundraiseService.createFund(input);
    await loadData();
  };

  const handleUpdateFund = async (fundId: string, input: UpdateFundInput) => {
    const updated = await fundraiseService.updateFund(fundId, input);
    // Optimistic: patch the fund in place so the open modal sees the new values
    // before loadData() reconciles from the server.
    setViewingFund((prev) => (prev && prev.id === fundId ? { ...prev, ...updated } : prev));
    await loadData();
  };

  // Pipeline share handlers
  const handleOpenShareModal = async (pipeline: Pipeline) => {
    setSharingPipeline(pipeline);
    try {
      const [links, members] = await Promise.all([
        fundraiseService.getShareLinks(pipeline.id),
        fundraiseService.getCollaborators(pipeline.id),
      ]);
      setShareLinks(links);
      setCollaborators(members);
    } catch (err) {
      console.error('Failed to load share state:', err);
      setShareLinks([]);
      setCollaborators([]);
    }
  };

  const handleCloseShareModal = () => {
    setSharingPipeline(null);
    setShareLinks([]);
    setCollaborators([]);
  };

  const handleCreateShareLink = async (
    permission: PipelineSharePermission
  ): Promise<PipelineShareLink> => {
    if (!sharingPipeline) throw new Error('No pipeline selected');
    const link = await fundraiseService.createShareLink(sharingPipeline.id, {
      permission_level: permission,
    });
    setShareLinks((prev) => [link, ...prev]);
    return link;
  };

  const handleToggleShareLink = async (linkId: string, isActive: boolean) => {
    if (!sharingPipeline) return;
    const updated = await fundraiseService.updateShareLink(sharingPipeline.id, linkId, isActive);
    setShareLinks((prev) => prev.map((l) => (l.id === linkId ? updated : l)));
  };

  const handleDeleteShareLink = async (linkId: string) => {
    if (!sharingPipeline) return;
    await fundraiseService.deleteShareLink(sharingPipeline.id, linkId);
    setShareLinks((prev) => prev.filter((l) => l.id !== linkId));
  };

  const handleRemoveCollaborator = async (collaboratorUserId: string) => {
    if (!sharingPipeline) return;
    await fundraiseService.removeCollaborator(sharingPipeline.id, collaboratorUserId);
    setCollaborators((prev) => prev.filter((c) => c.user_id !== collaboratorUserId));
    // Pipeline list may change (this user no longer sees the pipeline if they
    // were the one removed). Refresh.
    await loadData();
  };

  const handleMoveFund = async (fundId: string, newStage: string, position: number) => {
    // Find the fund and its pipeline
    let targetPipelineId: string | null = null;
    let originalFund: Fund | null = null;

    Object.entries(fundsByPipeline).forEach(([pipelineId, funds]) => {
      const fund = funds.find((f) => f.id === fundId);
      if (fund) {
        targetPipelineId = pipelineId;
        originalFund = fund;
      }
    });

    if (!targetPipelineId || !originalFund) return;

    // Optimistic update
    const updatedFunds = { ...fundsByPipeline };
    updatedFunds[targetPipelineId!] = updatedFunds[targetPipelineId!].map((f) =>
      f.id === fundId ? { ...f, stage: newStage, position } : f
    );
    setFundsByPipeline(updatedFunds);

    try {
      await fundraiseService.moveFund(fundId, newStage, position);
      // Recalculate stats using the updated funds state
      setStats(calculateStats(pipelines, updatedFunds));
    } catch (err) {
      // Revert on error
      setFundsByPipeline((prev) => {
        const updated = { ...prev };
        updated[targetPipelineId!] = updated[targetPipelineId!].map((f) =>
          f.id === fundId ? originalFund! : f
        );
        return updated;
      });
      console.error('Failed to move fund:', err);
    }
  };

  const handleDeleteFund = async () => {
    if (!deletingFund) return;
    setIsDeleting(true);
    try {
      await fundraiseService.deleteFund(deletingFund.id);
      setDeletingFund(null);
      await loadData();
    } catch (err) {
      console.error('Failed to delete fund:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Early returns for auth states
  if (authError) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4 text-red-600">Authentication Error</h1>
        <p className="text-red-500">{authError}</p>
      </div>
    );
  }

  if (!canAccessApp) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">Access Denied</h1>
        <p>You don&apos;t have access to this page.</p>
      </div>
    );
  }

  return (
    <>
      <div className="su-fundraise-page">
        {/* Header */}
        <PageHeader
          title="Fundraise Pipeline"
          subtitle="Track and manage your fundraising process across multiple rounds"
          action={
            <button
              className="su-btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <PlusIcon />
              <span>NEW PIPELINE</span>
            </button>
          }
        />

        {/* Stats */}
        <div className="su-fundraise-stats-grid">
          <StatCard
            icon={<LayersIcon />}
            label="Total Pipelines"
            value={loading ? '—' : stats.total_pipelines}
            colorTheme="purple"
            isLoading={loading}
          />
          <StatCard
            icon={<TargetIcon />}
            label="Total Funds Tracked"
            value={loading ? '—' : stats.total_funds}
            colorTheme="green"
            isLoading={loading}
          />
          <StatCard
            icon={<DollarIcon />}
            label="Combined Target"
            value={loading ? '—' : formatCurrency(stats.combined_target)}
            colorTheme="green"
            isLoading={loading}
          />
          <StatCard
            icon={<MessageIcon />}
            label="In Active Discussions"
            value={loading ? '—' : stats.active_discussions}
            colorTheme="purple"
            isLoading={loading}
          />
        </div>

        {/* Error state */}
        {error && (
          <div
            style={{
              color: '#f87171',
              fontSize: '14px',
              marginBottom: '16px',
              padding: '16px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            {error}
            <button
              className="su-btn-secondary"
              style={{ marginLeft: '16px' }}
              onClick={loadData}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="animate-pulse"
                style={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #1f1f1f',
                  padding: '24px',
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-6 w-40 bg-gray-800 rounded" />
                  <div className="h-5 w-24 bg-gray-800 rounded" />
                </div>
                <div className="flex gap-4">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-32 w-48 bg-gray-800 rounded" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && pipelines.length === 0 && (
          <div className="su-fundraise-empty">
            <div className="su-fundraise-empty-icon">
              <TargetIcon style={{ width: 48, height: 48 }} />
            </div>
            <h3 className="su-fundraise-empty-title">No pipelines yet</h3>
            <p className="su-fundraise-empty-subtitle">
              Create your first fundraising pipeline to start tracking investors
            </p>
            <button
              className="su-btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <PlusIcon />
              <span>Create Pipeline</span>
            </button>
          </div>
        )}

        {/* Pipeline list */}
        {!loading &&
          pipelines.map((pipeline) => {
            // For pipelines the user collaborates on (does not own), hide
            // owner-only affordances: Delete pipeline, Share pipeline, and
            // per-fund Delete. Edit/Move/Add remain available.
            const isShared = !!pipeline.is_collaborator;
            return (
              <PipelineSection
                key={pipeline.id}
                pipeline={pipeline}
                funds={fundsByPipeline[pipeline.id] || []}
                onToggleExpand={handleTogglePipelineExpand}
                onEdit={(p) => setEditingPipeline(p)}
                onDelete={isShared ? undefined : (p) => setDeletingPipeline(p)}
                onAddFund={(p) => setAddingFundToPipeline(p)}
                onMoveFund={handleMoveFund}
                onDeleteFund={isShared ? undefined : (f) => setDeletingFund(f)}
                onFundClick={(f) => setViewingFund(f)}
                onShare={isShared ? undefined : handleOpenShareModal}
              />
            );
          })}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreatePipelineModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePipeline}
        />
      )}

      {editingPipeline && (
        <EditPipelineModal
          pipeline={editingPipeline}
          funds={fundsByPipeline[editingPipeline.id] || []}
          onClose={() => setEditingPipeline(null)}
          onSubmit={handleUpdatePipeline}
        />
      )}

      {addingFundToPipeline && (
        <AddFundModal
          pipeline={addingFundToPipeline}
          onClose={() => setAddingFundToPipeline(null)}
          onSubmit={handleAddFund}
        />
      )}

      {viewingFund && (() => {
        const owningPipeline = pipelines.find((p) => p.id === viewingFund.pipeline_id);
        if (!owningPipeline) return null;
        return (
          <FundDetailModal
            fund={viewingFund}
            pipeline={owningPipeline}
            onClose={() => setViewingFund(null)}
            onSave={handleUpdateFund}
          />
        );
      })()}

      {sharingPipeline && (
        <PipelineShareModal
          pipeline={sharingPipeline}
          shareLinks={shareLinks}
          collaborators={collaborators}
          onClose={handleCloseShareModal}
          onCreateLink={handleCreateShareLink}
          onToggleLink={handleToggleShareLink}
          onDeleteLink={handleDeleteShareLink}
          onRemoveCollaborator={handleRemoveCollaborator}
        />
      )}

      {deletingPipeline && (
        <DeleteConfirmModal
          title="Delete Pipeline"
          message={`Are you sure you want to delete "${deletingPipeline.name}"? This will also delete all funds in this pipeline. This action cannot be undone.`}
          onCancel={() => setDeletingPipeline(null)}
          onConfirm={handleDeletePipeline}
          isDeleting={isDeleting}
        />
      )}

      {deletingFund && (
        <DeleteConfirmModal
          title="Delete Fund"
          message={`Are you sure you want to delete "${deletingFund.name}"? This action cannot be undone.`}
          onCancel={() => setDeletingFund(null)}
          onConfirm={handleDeleteFund}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};

export default Fundraise;
